import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import ShareCard from '../components/ShareCard';
import Logo from '../components/Logo';
import { LogOut, Sparkles, ArrowLeft, Share2, ChevronDown, ChevronUp } from 'lucide-react';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';

const safeText = (value) => (typeof value === 'string' ? value : '');

const TarotPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [user, setUser] = useState(null);
  const [reading, setReading] = useState(null);
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareContent, setShareContent] = useState(null);

  const fetchHistory = async (currentLanguage = language) => {
    setHistoryLoading(true);
    try {
      const response = await api.get('/universe/history');
      const all = Array.isArray(response.data) ? response.data : [];
      const tarotOnly = all.filter(
        (item) =>
          item?.spread_type ||
          item?.cards ||
          item?.interpretation
      );
      setHistory(tarotOnly);
    } catch (error) {
      console.error('Failed to fetch tarot history', error);
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
    } catch (error) {
      console.error('Failed to parse user data', error);
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      navigate('/login');
      return;
    }

    fetchHistory(language);
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) return;
    fetchHistory(language);
  }, [language]);

  const handleDrawTarot = async () => {
    if (loading) return;

    setLoading(true);
    setReading(null);

    try {
      const response = await api.post('/universe/tarot', {
        language,
      });

      const data = response?.data;
      if (!data) {
        throw new Error('Empty tarot response');
      }

      setReading(data);
      await fetchHistory(language);

      setTimeout(() => {
        document.getElementById('tarot-reading-section')?.scrollIntoView({
          behavior: 'smooth',
        });
      }, 100);
    } catch (error) {
      console.error('Tarot draw failed:', error);
      toast.error(
        error?.response?.data?.detail ||
        error.message ||
        'Failed to draw tarot'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
    } catch (error) {
      console.error('Logout API call failed:', error);
    }

    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  const buildShareContent = (item) => {
    const cards = Array.isArray(item?.cards)
      ? item.cards
          .map((card) => {
            const name = safeText(card?.name);
            const position = safeText(card?.position);
            return position ? `${name} (${position})` : name;
          })
          .filter(Boolean)
          .join(', ')
      : '';

    const interpretation = safeText(item?.interpretation);

    return [
      cards ? `${t('tarot')}: ${cards}` : '',
      interpretation,
    ]
      .filter(Boolean)
      .join('\n\n');
  };

  if (!user) return null;

  return (
    <div className="min-h-screen relative">
      <CosmicBackground />
      <div
        className="fixed inset-0 bg-gradient-to-b from-[#3b0764] via-[#0f172a] to-black pointer-events-none"
        style={{ zIndex: 1 }}
      />

      <div className="relative z-10">
        <header
          className="px-6 sm:px-12 border-b border-white/5"
          style={{
            paddingTop: '20px',
            paddingBottom: '24px'
          }}
       >
          <div className="max-w-7xl mx-auto flex justify-between items-center">
            <div className="flex items-center gap-6">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="back-button"
              >
                <ArrowLeft size={20} />
              </button>
              <Logo size="md" />
            </div>

            <div className="flex items-center gap-6">
              <LanguageSelector />
              <span className="text-slate-300 text-sm hidden sm:inline" data-testid="user-name">
                {t('welcome')}, {user.full_name}
              </span>
              <button
                onClick={handleLogout}
                className="text-slate-400 hover:text-white transition-colors"
                data-testid="logout-button"
                title={t('logout')}
              >
                <LogOut size={20} />
              </button>
            </div>
          </div>
        </header>

        <div className="max-w-5xl mx-auto px-6 py-12 space-y-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center space-y-4"
          >
            <h1 className="font-heading text-4xl font-light text-white tracking-wide">
              {t('tarot')}
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto leading-relaxed">
              {t('tarotCta')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="glass-card p-8 text-center"
            data-testid="tarot-draw-card"
          >
            <div className="flex justify-center mb-4">
              <Sparkles className="w-10 h-10 text-amber-400" />
            </div>

            <button
              onClick={handleDrawTarot}
              disabled={loading}
              className="px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 rounded-full font-bold text-lg hover:from-amber-400 hover:to-amber-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
              data-testid="draw-tarot-button"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-900" />
                  <span>{t('processing')}</span>
                </>
              ) : (
                <>
                  <Sparkles size={18} />
                  <span>{t('tarot')}</span>
                </>
              )}
            </button>
          </motion.div>

          <AnimatePresence>
            {reading && (
              <motion.div
                id="tarot-reading-section"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="space-y-6"
              >
                <div className="flex justify-end">
                  <button
                    onClick={() => {
                      setShareContent({
                        title: t('tarot'),
                        content: buildShareContent(reading),
                        date: reading.created_at,
                        type: 'tarot',
                      });
                      setShareModalOpen(true);
                    }}
                    className="px-4 py-2 bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 rounded-lg transition-all flex items-center gap-2 text-sm"
                    data-testid="share-tarot-btn"
                  >
                    <Share2 size={16} />
                    <span>{t('share')}</span>
                  </button>
                </div>

                {Array.isArray(reading.cards) && reading.cards.length > 0 && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {reading.cards.map((card, index) => (
                      <motion.div
                        key={`${card?.name || 'card'}-${index}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="glass-card p-6 text-center"
                      >
                        <p className="text-amber-400 font-bold text-lg mb-2">
                          {safeText(card?.name)}
                        </p>
                        {safeText(card?.position) && (
                          <p className="text-slate-500 text-sm mb-2">
                            {safeText(card?.position)}
                          </p>
                        )}
                        <p className="text-slate-300 text-sm">
                          {safeText(card?.meaning)}
                        </p>
                      </motion.div>
                    ))}
                  </div>
                )}

                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.25 }}
                  className="glass-card p-8"
                  data-testid="tarot-interpretation-card"
                >
                  <h3 className="font-heading text-2xl font-bold text-gradient-gold mb-4">
                    {t('theUniverseSpeaks')}
                  </h3>
                  <p className="text-slate-300 leading-relaxed text-lg">
                    {safeText(reading.interpretation)}
                  </p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          {history.length > 0 && (
            <div className="text-center">
              <button
                onClick={() => setShowHistory(!showHistory)}
                className="text-violet-400 hover:text-violet-300 transition-colors font-medium flex items-center gap-2 mx-auto"
                data-testid="toggle-history-button"
              >
                {showHistory ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                <span>{showHistory ? t('hideHistory') : t('viewHistory')} {t('tarot')}</span>
              </button>
            </div>
          )}

          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-4"
                data-testid="history-section"
              >
                {historyLoading && (
                  <div className="glass-card p-6 text-center text-slate-400">
                    {t('loading') || 'Loading...'}
                  </div>
                )}

                {!historyLoading && history.length > 0 && history.map((item, idx) => (
                  <motion.div
                    key={item.id || idx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="glass-card p-6"
                    data-testid={`history-item-${idx}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <p className="text-white font-medium flex-1">
                        {Array.isArray(item.cards)
                          ? item.cards.map((card) => safeText(card?.name)).filter(Boolean).join(', ')
                          : t('tarot')}
                      </p>

                      <button
                        onClick={() => {
                          setShareContent({
                            title: t('tarot'),
                            content: buildShareContent(item),
                            date: item.created_at,
                            type: 'tarot',
                          });
                          setShareModalOpen(true);
                        }}
                        className="p-1.5 text-slate-500 hover:text-violet-400 hover:bg-violet-500/10 rounded-lg transition-all ml-2 flex-shrink-0"
                        data-testid={`share-tarot-history-${idx}`}
                        title={t('share')}
                      >
                        <Share2 size={14} />
                      </button>
                    </div>

                    <p className="text-slate-400 text-sm mb-2">
                      {safeText(item.interpretation)}
                    </p>

                    <p className="text-slate-600 text-xs">
                      {item.created_at ? new Date(item.created_at).toLocaleString() : ''}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ShareCard
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        title={shareContent?.title || ''}
        content={shareContent?.content || ''}
        date={shareContent?.date || new Date().toISOString()}
        type={shareContent?.type || 'tarot'}
      />
    </div>
  );
};

export default TarotPage;
